import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Monitor, Smartphone, Cpu, MemoryStick, HardDrive, MapPin, Calendar, Copy, Check, Hash, Ruler, Phone, Layers } from 'lucide-react';
import { Keyboard, FileText, Box } from './icons';
import EstadoActaCell from './EstadoActaCell';
import EmpresaBadge from './EmpresaBadge';
import { formatUbicacion } from '../utils/locationUtils';
import { buildActaHistoricaUrl } from '../config';

const ESTADO_COLOR = {
  disponible: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  asignado: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  'en mantenimiento': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  retirado: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

const iconoCategoria = (categoria) => {
  switch (categoria?.toLowerCase()) {
    case 'equipos': return <Monitor className="w-6 h-6" />;
    case 'móviles': case 'moviles': return <Smartphone className="w-6 h-6" />;
    case 'monitores': return <Keyboard className="w-6 h-6" />;
    default: return <Box className="w-6 h-6" />;
  }
};

// Datos técnicos que le sirven a la persona según el tipo de activo.
const especificaciones = (a) => {
  const cat = a.categoria?.toLowerCase();
  const lista = [];
  if (cat === 'equipos') {
    lista.push([Cpu, 'Procesador', a.procesador], [MemoryStick, 'Memoria RAM', a.ram], [HardDrive, 'Almacenamiento', a.almacenamiento], [Layers, 'Sistema operativo', a.sistemaOperativo]);
  } else if (cat === 'móviles' || cat === 'moviles') {
    lista.push([Phone, 'Número', a.numeroCelular], [Hash, 'IMEI', a.imei], [HardDrive, 'Capacidad', a.capacidad]);
  } else if (cat === 'monitores') {
    lista.push([Ruler, 'Pulgadas', a.pulgadas ? `${a.pulgadas}"` : '']);
  }
  lista.push([Hash, 'N° de serie', a.serie]);
  return lista.filter(([, , valor]) => valor && String(valor).trim());
};

const haceCuanto = (fecha) => {
  if (!fecha) return '';
  const dias = Math.floor((Date.now() - new Date(fecha).getTime()) / 86400000);
  if (dias < 1) return 'hoy';
  if (dias < 31) return `hace ${dias} día${dias === 1 ? '' : 's'}`;
  const meses = Math.floor(dias / 30.44);
  if (meses < 12) return `hace ${meses} mes${meses === 1 ? '' : 'es'}`;
  const anios = Math.floor(meses / 12);
  const resto = meses % 12;
  return `hace ${anios} año${anios === 1 ? '' : 's'}${resto ? ` y ${resto} mes${resto === 1 ? '' : 'es'}` : ''}`;
};

export default function MisActivoCard({ activo, acta, horizontal }) {
  const navigate = useNavigate();
  const [copiado, setCopiado] = useState(false);
  const specs = especificaciones(activo);
  const modelo = [activo.marca, activo.modelo].filter(Boolean).join(' ');
  const ubicacion = activo.ubicacion ? formatUbicacion(activo.ubicacion) : '';
  const fecha = activo.fechaAsignacion ? new Date(activo.fechaAsignacion) : null;

  const copiarCodigo = async () => {
    try {
      await navigator.clipboard.writeText(activo.codigo);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch { /* sin permiso de portapapeles */ }
  };

  const verActa = () => {
    if (acta.esHistorica && acta.rutaArchivo) window.open(buildActaHistoricaUrl(acta.rutaArchivo), '_blank');
    else navigate(`/actas/previsualizar-firmado/${acta.id}`);
  };

  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow overflow-hidden flex flex-col">
      <div className="p-5 flex-1">
        <div className="flex items-start gap-3">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-blue-600 dark:text-blue-400 shrink-0">
            {iconoCategoria(activo.categoria)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-bold text-lg text-gray-900 dark:text-white tracking-tight truncate">{activo.codigo}</h4>
              <button onClick={copiarCodigo} title="Copiar código" className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                {copiado ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {activo.categoria}{modelo ? ` · ${modelo}` : ''}
            </p>
          </div>
          <span className={`inline-flex px-2 py-1 rounded text-[10px] font-semibold tracking-wide uppercase shrink-0 ${ESTADO_COLOR[activo.estado?.toLowerCase()] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>
            {activo.estado}
          </span>
        </div>

        {specs.length > 0 && (
          <dl className={`mt-4 grid gap-x-4 gap-y-3 ${horizontal ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2'}`}>
            {specs.map(([Icono, etiqueta, valor]) => (
              <div key={etiqueta} className="min-w-0">
                <dt className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  <Icono className="w-3.5 h-3.5" />{etiqueta}
                </dt>
                <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white break-words" title={String(valor)}>{valor}</dd>
              </div>
            ))}
          </dl>
        )}

        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-2 text-sm">
          {ubicacion && (
            <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
              <MapPin className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
              <span className="break-words">{ubicacion}</span>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-gray-700 dark:text-gray-300">
            {fecha && (
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                Asignado el {fecha.toLocaleDateString('es-CL')} <span className="text-gray-400">({haceCuanto(fecha)})</span>
              </span>
            )}
            {activo.empresa && <EmpresaBadge empresa={activo.empresa} />}
          </div>
        </div>
      </div>

      <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center">
          <FileText className="w-4 h-4 mr-1.5" />Acta
        </span>
        <div className="flex items-center gap-3">
          <EstadoActaCell acta={acta} />
          {acta && (
            <button onClick={verActa} className="px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20">
              Ver acta
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
